import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BOOKING, CONSULTANT_PROFILE } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import StarRating from '@/components/StarRating';
import LockedConsultants from '@/components/LockedConsultants';
import ProgressChecklist from '@/components/ProgressChecklist';
import { Users, Mail, Building2, Phone, FileSearch, ArrowRight, Briefcase, Layers } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Consultants() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    service_type: 'Building Defects',
    preferred_date: '',
    description: '',
    budget: 150.0
  });

  const fetchAll = useCallback(async () => {
    try {
      const [accessRes, servicesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/user/consultant-access`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services-catalog`),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setAccess(accessRes.data);
      setServicesCatalog(servicesRes.data);
      setBookings(bookingsRes.data || []);

      if (accessRes.data.has_access) {
        const consultantsRes = await axios.get(`${API}/consultants`);
        setConsultants(consultantsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch consultants page data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleBookConsultant = (consultant) => {
    setSelectedConsultant(consultant);
    setShowBookingModal(true);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/bookings`,
        {
          ...bookingForm,
          consultant_email: selectedConsultant.email
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowBookingModal(false);
      setBookingForm({
        service_type: 'Building Defects',
        preferred_date: '',
        description: '',
        budget: 150.0
      });
      window.location.href = '/bookings';
    } catch (error) {
      console.error('Failed to create booking:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-black mb-2" style={{fontFamily: 'Outfit'}}>
            Expert Consultants
          </h1>
          <p className="text-slate-600">Find and book construction professionals</p>
        </div>

        {/* Your Progress — same workflow checklist as on the Dashboard, in banner
            format. Reassures the user that the gated experience is part of a
            structured A→B→C process before they reach the consultants. */}
        <div className="mb-8">
          <ProgressChecklist bookings={bookings} variant="banner" />
        </div>

        {/* === Project Management Services — always visible (locked or unlocked) === */}
        <div
          data-testid="project-management-services-panel"
          className="grid-border bg-white p-6 mb-8"
        >
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
                Project Management Services
              </h2>
              <p className="text-sm text-slate-600">
                Hands-on project leadership across two sectors that match our 20+ years of on-the-tools experience —
                see the <button onClick={() => navigate('/about')} className="text-blue-700 hover:underline">About page</button> for the full track record.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Strata Services */}
            <article
              data-testid="pm-strata-services-card"
              className="grid-border bg-sky-50 border-sky-200 p-5"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 bg-sky-600 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-black" style={{ fontFamily: 'Outfit' }}>
                    Strata Services
                  </h3>
                  <p className="text-xs text-sky-800 uppercase tracking-wider font-semibold mt-0.5">
                    For owners corporations &amp; committees
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-700 mb-3">
                Strata building defect inspections, scoped repairs, contractor coordination and
                stage-managed rectification — keeping committees, residents and trades aligned.
              </p>
              <ul className="text-xs text-slate-600 space-y-1.5 mb-4">
                <li className="flex items-start gap-2"><span className="text-sky-600 mt-0.5">•</span>Strata defect inspections &amp; reports</li>
                <li className="flex items-start gap-2"><span className="text-sky-600 mt-0.5">•</span>Scoped repair works &amp; trade coordination</li>
                <li className="flex items-start gap-2"><span className="text-sky-600 mt-0.5">•</span>Owners' committee progress updates</li>
                <li className="flex items-start gap-2"><span className="text-sky-600 mt-0.5">•</span>Approvals, completion sign-offs &amp; close-out</li>
                <li className="flex items-start gap-2"><span className="text-sky-600 mt-0.5">•</span>Guidance, help &amp; written recommendations</li>
              </ul>
              <button
                data-testid="pm-strata-enquire"
                onClick={() => navigate('/bookings')}
                className="w-full px-4 py-2 bg-black text-white text-sm font-medium rounded-sm hover:bg-slate-800 inline-flex items-center justify-center gap-2"
              >
                Enquire about Strata PM
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </article>

            {/* Commercial */}
            <article
              data-testid="pm-commercial-card"
              className="grid-border bg-emerald-50 border-emerald-200 p-5"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 bg-emerald-600 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-black" style={{ fontFamily: 'Outfit' }}>
                    Commercial
                  </h3>
                  <p className="text-xs text-emerald-800 uppercase tracking-wider font-semibold mt-0.5">
                    Warehouses · retail · mixed-use builds
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-700 mb-3">
                Commercial builds delivered clean — from first review through approvals to
                completion, including defects rectification and ongoing operational guidance.
              </p>
              <ul className="text-xs text-slate-600 space-y-1.5 mb-4">
                <li className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">•</span>Pre-construction reviews &amp; cost reality-checks</li>
                <li className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">•</span>Approvals, council &amp; certifier coordination</li>
                <li className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">•</span>Programme &amp; trade sequencing oversight</li>
                <li className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">•</span>Defects identification, scoping &amp; rectification</li>
                <li className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">•</span>Completion sign-off, help &amp; handover guidance</li>
              </ul>
              <button
                data-testid="pm-commercial-enquire"
                onClick={() => navigate('/bookings')}
                className="w-full px-4 py-2 bg-black text-white text-sm font-medium rounded-sm hover:bg-slate-800 inline-flex items-center justify-center gap-2"
              >
                Enquire about Commercial PM
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </article>
          </div>

          <p className="text-[11px] text-slate-500 mt-4 italic">
            Project Management engagements are scoped individually after an initial pre-assessment.
            Use Bookings to start the conversation.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        ) : access && !access.has_access ? (
          <LockedConsultants
            stagesCompleted={access.stages_completed}
            servicesCatalog={servicesCatalog}
          />
        ) : consultants.length === 0 ? (
          <div className="grid-border bg-white p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold mb-2" style={{fontFamily: 'Outfit'}}>No consultants available</h3>
            <p className="text-slate-600">Check back later for available professionals</p>
          </div>
        ) : (
          <>
            {/* Post-workflow gated services — visible only once access is granted */}
            {servicesCatalog.some((s) => s.requires_workflow_complete) && (
              <div data-testid="follow-up-services-panel" className="grid-border bg-amber-50 border-amber-200 p-6 mb-8">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <FileSearch className="w-5 h-5 text-amber-800" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-black" style={{fontFamily: 'Outfit'}}>
                      Specialist Services — Referral &amp; Report Explanation
                    </h3>
                    <p className="text-sm text-slate-600">
                      Now that your initial report is issued, you can request a referral and summary
                      review for any of the specialist report types below.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { name: 'Roofing Reports', description: 'Referral & summary review of independent roofing specialist reports — defects, leaks, structural integrity, and rectification advice.' },
                    { name: 'Plumbing Leak Test & Reports', description: 'Referral & summary review of plumbing leak-detection tests and licensed plumber reports — water ingress, pressure tests, and drainage findings.' },
                    { name: 'Electrical & Solar Reports', description: 'Referral & summary review of licensed electrical and solar specialist reports — safety, compliance, and performance findings.' },
                    { name: 'Asbestos Sampling & Reports', description: 'Referral & summary review of NATA-accredited asbestos sampling and initial assessment reports — identification, risk and removal advice.' },
                    { name: 'Building Reports', description: 'Referral & summary review of independent building reports — pre-purchase, dilapidation, handover, and defect inspections.' }
                  ].map((item) => (
                    <div key={item.name} data-testid="follow-up-service-card" className="grid-border bg-white p-4">
                      <h5 className="font-semibold text-black mb-1">{item.name}</h5>
                      <p className="text-xs text-slate-600 mb-3">{item.description}</p>
                      <div className="flex items-center justify-end">
                        <button
                          data-testid="request-follow-up-service"
                          onClick={() => navigate('/bookings')}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-xs font-medium"
                        >
                          Request Referral
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div data-testid="follow-up-note" className="md:col-span-2 grid-border bg-white border-amber-300 p-4">
                    <p className="text-xs text-slate-700 leading-relaxed">
                      <strong className="text-amber-900">To be noted —</strong> All specialist services
                      referral &amp; reports above are based on <strong>referral and explanation only</strong>.
                      Our cost is therefore purely a <strong>referral and review service</strong>. Once a
                      deposit is made and the specialist report is requested, we can provide a simpler
                      &amp; summary review once the available specialist report is received. These
                      specialist reports are <strong>not subject to any lead time</strong> and can only
                      be advised once we confirm availability with our key external consultants.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {consultants.map((consultant) => (
              <div key={consultant.id} className="grid-border bg-white p-6 hover-lift">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-sm flex items-center justify-center">
                    <Users className="w-8 h-8 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold truncate" style={{fontFamily: 'Outfit'}}>
                      {consultant.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <StarRating value={consultant.ratings_avg || 0} size="sm" />
                      <span className="text-xs text-slate-500">
                        {consultant.ratings_count > 0
                          ? `${consultant.ratings_avg} (${consultant.ratings_count})`
                          : 'New'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{consultant.email}</span>
                  </div>
                  {consultant.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4" />
                      <span>{consultant.phone}</span>
                    </div>
                  )}
                  {consultant.organization && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="w-4 h-4" />
                      <span className="truncate">{consultant.organization}</span>
                    </div>
                  )}
                  {consultant.hourly_rate && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="font-medium text-black">A${consultant.hourly_rate.toFixed(0)}/hr</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid={CONSULTANT_PROFILE.viewProfileButton}
                    onClick={() => navigate(`/consultants/${consultant.id}`)}
                    className="py-3 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all font-medium text-sm"
                  >
                    View Profile
                  </button>
                  <button
                    data-testid={BOOKING.createButton}
                    onClick={() => handleBookConsultant(consultant)}
                    className="py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all font-medium text-sm"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))}
            </div>
          </>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedConsultant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-bold mb-6" style={{fontFamily: 'Outfit'}}>
              Book with {selectedConsultant.name}
            </h2>
            <form onSubmit={handleSubmitBooking} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-2">Service Type</label>
                <select
                  data-testid={BOOKING.serviceTypeSelect}
                  value={bookingForm.service_type}
                  onChange={(e) => setBookingForm({...bookingForm, service_type: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                >
                  <option value="Building Defects">Building Defects</option>
                  <option value="Specialist Services">Specialist Services</option>
                  <option value="Construction Sequencing">Construction Sequencing</option>
                  <option value="Cost Management">Cost Management</option>
                  <option value="DIY Projects">DIY Projects</option>
                  <option value="Trade Works & Administration" disabled>Trade Works & Administration — Coming Soon</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Preferred Date</label>
                <input
                  data-testid={BOOKING.dateInput}
                  type="date"
                  value={bookingForm.preferred_date}
                  onChange={(e) => setBookingForm({...bookingForm, preferred_date: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Description</label>
                <textarea
                  data-testid={BOOKING.descriptionInput}
                  value={bookingForm.description}
                  onChange={(e) => setBookingForm({...bookingForm, description: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  rows="4"
                  placeholder="Describe your needs..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Budget (AUD)</label>
                <input
                  data-testid={BOOKING.budgetInput}
                  type="number"
                  step="0.01"
                  value={bookingForm.budget}
                  onChange={(e) => setBookingForm({...bookingForm, budget: parseFloat(e.target.value)})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
                >
                  Book Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}